import React, { useState } from "react";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import { Spinner, StatusBadge } from "@/components/Atoms";
import { fmtRel } from "@/lib/format";
import { HardDriveUpload, Plus, Trash2, PlayCircle, X, Server } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

const fmtBytes = (n) => {
    n = Number(n);
    if (!Number.isFinite(n) || n < 0) return "—";
    const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0;
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
};
const fmtDur = (a, b) => {
    if (!a) return null;
    const end = b ? new Date(b) : new Date();
    const s = Math.max(0, Math.round((end - new Date(a)) / 1000));
    const m = Math.floor(s / 60);
    return m ? `${m}m ${s % 60}s` : `${s}s`;
};
const PHASE_LABEL = {
    queued: "Queued", provision: "Provisioning VM",
    inject: "Preparing", preflight: "Target check",
    scratch: "Creating scratch", "download-s3": "S3 download",
    "dump-db": "DB dump", "dump-stalwart": "Stalwart DB",
    "download-stalwart-s3": "Stalwart S3", config: "Configuration",
    readable: "Readable export", zip: "Archiving", upload: "Upload to target",
    cleanup: "Cleanup", done: "Done", error: "Error",
};

// Nicht-geheime Felder je Zieltyp (Pflichtfelder mit req:true) — spiegelt
// core/backup_targets.py:_REQUIRED wider.
const TYPE_FIELDS = {
    ftp: [{ k: "host", req: true }, { k: "port", ph: "21" }, { k: "user", req: true }],
    ftps: [{ k: "host", req: true }, { k: "port", ph: "990" }, { k: "user", req: true }],
    sftp: [{ k: "host", req: true }, { k: "port", ph: "22" }, { k: "user", req: true }],
    s3: [
        { k: "endpoint", req: true, ph: "https://s3.example.com" },
        { k: "bucket", req: true }, { k: "access_key", req: true },
        { k: "region", ph: "us-east-1" }, { k: "provider", ph: "Other" },
    ],
    webdav: [
        { k: "url", req: true, ph: "https://cloud.example.com/remote.php/dav/files/user" },
        { k: "user", req: true }, { k: "vendor", ph: "nextcloud / other" },
    ],
};
const TYPE_LABEL = { ftp: "FTP", ftps: "FTPS (TLS)", sftp: "SFTP", s3: "S3", webdav: "WebDAV" };
const FIELD_LABEL = {
    host: "Host", port: "Port", user: "User", endpoint: "Endpoint",
    bucket: "Bucket", access_key: "Access Key", region: "Region",
    provider: "Provider", url: "URL", vendor: "Vendor",
};

const EMPTY = {
    type: "sftp", name: "", host: "", port: "", user: "", endpoint: "",
    bucket: "", access_key: "", region: "", provider: "", url: "", vendor: "",
    secret: "", dest_path: "", schedule: "none", export_mode: "raw",
};

export default function ExternalBackupPanel({ cloudId }) {
    const targetsUrl = `/api/admin/clouds/${cloudId}/backup-targets`;
    const runsUrl = `/api/admin/clouds/${cloudId}/backup-runs`;
    const { data: tData, mutate: mutTargets, isLoading: tLoading } = useSWR(targetsUrl, fetcher);
    const { data: rData, mutate: mutRuns } = useSWR(runsUrl, fetcher, { refreshInterval: 4000 });

    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [runBusy, setRunBusy] = useState(null);
    const [err, setErr] = useState(null);
    const [logCache, setLogCache] = useState({});

    const loadLog = async (runId) => {
        if (logCache[runId] !== undefined) return;
        try {
            const d = (await api.get(`${runsUrl}/${runId}`)).data;
            setLogCache((c) => ({ ...c, [runId]: d.vm_log || "(no log)" }));
        } catch (e) {
            setLogCache((c) => ({ ...c, [runId]: `Could not load log: ${formatApiError(e)}` }));
        }
    };

    const targets = tData?.targets || [];
    const runs = rData?.runs || [];
    const secretLabel = form.type === "s3" ? "Secret Key" : "Password";
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const saveTarget = async () => {
        setBusy(true); setErr(null);
        try {
            const body = { type: form.type, schedule: form.schedule, export_mode: form.export_mode };
            if (form.name) body.name = form.name;
            if (form.dest_path) body.dest_path = form.dest_path;
            if (form.secret) body.secret = form.secret;
            for (const f of TYPE_FIELDS[form.type]) if (form[f.k]) body[f.k] = form[f.k];
            await api.post(targetsUrl, body);
            setForm(EMPTY); setShowForm(false);
            await mutTargets();
        } catch (e) { setErr(formatApiError(e)); }
        setBusy(false);
    };

    const deleteTarget = async (id) => {
        if (!window.confirm("Delete this backup target?")) return;
        try { await api.delete(`${targetsUrl}/${id}`); await mutTargets(); }
        catch (e) { setErr(formatApiError(e)); }
    };

    const runBackup = async (t) => {
        setRunBusy(t.id); setErr(null);
        try {
            await api.post(runsUrl, { target_id: t.id });
            await mutRuns();
        } catch (e) { setErr(formatApiError(e)); }
        setRunBusy(null);
    };

    const deleteRun = async (runId) => {
        if (!window.confirm("Delete this failed backup run?")) return;
        setErr(null);
        try { await api.delete(`${runsUrl}/${runId}`); await mutRuns(); }
        catch (e) { setErr(formatApiError(e)); }
    };

    const runStatus = (s) =>
        s === "done" ? "active" : (s === "running" || s === "queued") ? "provisioning"
            : s === "error" ? "failed" : s;

    return (
        <div className="cm-card p-5" data-testid="external-backup-panel">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <HardDriveUpload size={14} className="text-indigo-300" />
                    <div className="cm-h3">External backup</div>
                </div>
                <button
                    onClick={() => { setShowForm((v) => !v); setErr(null); }}
                    className="cm-btn-ghost text-xs flex items-center gap-1.5"
                    data-testid="ext-backup-add-target-toggle"
                >
                    {showForm ? <><X size={12} /> Cancel</> : <><Plus size={12} /> New target</>}
                </button>
            </div>
            <div className="text-xs text-zinc-500 mb-3">
                Customer-owned backup targets (FTP/FTPS/SFTP/S3/WebDAV). A run
                starts a temporary backup VM, backs up the cloud and uploads the
                archive encrypted to the target. Credentials are stored encrypted.
            </div>
            {err && <div className="text-xs text-cm-danger mb-2" data-testid="ext-backup-error">{err}</div>}

            {showForm && (
                <div className="bg-zinc-950 border border-zinc-800 p-3 mb-3 space-y-2" data-testid="ext-backup-form">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <label className="block">
                            <span className="cm-label">Type</span>
                            <select className="cm-input text-xs w-full" value={form.type}
                                onChange={(e) => set("type", e.target.value)} data-testid="ext-backup-type">
                                {Object.keys(TYPE_FIELDS).map((t) => (
                                    <option key={t} value={t}>{TYPE_LABEL[t]}</option>
                                ))}
                            </select>
                        </label>
                        <label className="block sm:col-span-2">
                            <span className="cm-label">Name (optional)</span>
                            <input className="cm-input text-xs w-full" value={form.name}
                                onChange={(e) => set("name", e.target.value)}
                                placeholder={`${form.type}-target`} data-testid="ext-backup-name" />
                        </label>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {TYPE_FIELDS[form.type].map((f) => (
                            <label className="block" key={f.k}>
                                <span className="cm-label">{FIELD_LABEL[f.k]}{f.req ? " *" : ""}</span>
                                <input className="cm-input text-xs w-full" value={form[f.k]}
                                    onChange={(e) => set(f.k, e.target.value)} placeholder={f.ph || ""}
                                    data-testid={`ext-backup-${f.k}`} />
                            </label>
                        ))}
                        <label className="block">
                            <span className="cm-label">{secretLabel} *</span>
                            <input type="password" className="cm-input text-xs w-full" value={form.secret}
                                onChange={(e) => set("secret", e.target.value)}
                                autoComplete="new-password" data-testid="ext-backup-secret" />
                        </label>
                        <label className="block">
                            <span className="cm-label">Target path (optional)</span>
                            <input className="cm-input text-xs w-full" value={form.dest_path}
                                onChange={(e) => set("dest_path", e.target.value)}
                                placeholder="backups/cloud-x" data-testid="ext-backup-dest-path" />
                        </label>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <label className="block">
                            <span className="cm-label">Schedule</span>
                            <select className="cm-input text-xs w-full" value={form.schedule}
                                onChange={(e) => set("schedule", e.target.value)} data-testid="ext-backup-schedule">
                                <option value="none">Manual</option>
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="cm-label">Export mode</span>
                            <select className="cm-input text-xs w-full" value={form.export_mode}
                                onChange={(e) => set("export_mode", e.target.value)} data-testid="ext-backup-mode">
                                <option value="raw">Raw (fast)</option>
                                <option value="readable">Readable (decrypted)</option>
                                <option value="both">Both</option>
                            </select>
                        </label>
                        <div className="flex items-end">
                            <button onClick={saveTarget} disabled={busy}
                                className="cm-btn-primary text-xs w-full flex items-center justify-center gap-1.5 disabled:opacity-40"
                                data-testid="ext-backup-save-target">
                                <Plus size={12} /> Save target
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Ziele */}
            {tLoading ? <Spinner /> : !targets.length ? (
                <div className="text-xs text-zinc-500 py-3 text-center bg-zinc-950 border border-zinc-800" data-testid="ext-backup-no-targets">
                    No external backup targets yet
                </div>
            ) : (
                <div className="space-y-2">
                    {targets.map((t) => (
                        <div key={t.id}
                            className="bg-zinc-950 border border-zinc-800 p-2 text-xs flex justify-between items-center"
                            data-testid={`ext-backup-target-${t.id}`}>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-zinc-100">{t.name}</span>
                                    <span className="font-mono text-[11px] text-indigo-300 border border-indigo-700/40 px-1 uppercase">{t.type}</span>
                                    {t.schedule && t.schedule !== "none" && (
                                        <span className="font-mono text-[11px] text-amber-300">{t.schedule}</span>
                                    )}
                                    <span className="font-mono text-[11px] text-zinc-500">{t.export_mode}</span>
                                </div>
                                <div className="font-mono text-[11px] text-zinc-500 mt-0.5 truncate">
                                    {t.config?.host || t.config?.endpoint || t.config?.url || "—"}
                                    {t.dest_path ? ` · /${t.dest_path}` : ""}
                                    {t.last_run_at ? ` · last ${fmtRel(t.last_run_at)}` : ""}
                                    {t.last_status ? ` · ${t.last_status}` : ""}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button onClick={() => runBackup(t)} disabled={runBusy === t.id}
                                    className="cm-btn-ghost text-xs flex items-center gap-1 text-emerald-300 disabled:opacity-40"
                                    data-testid={`ext-backup-run-${t.id}`}>
                                    <PlayCircle size={11} /> {runBusy === t.id ? "…" : "Start backup"}
                                </button>
                                <button onClick={() => deleteTarget(t.id)}
                                    className="cm-btn-ghost text-xs flex items-center gap-1 text-cm-danger"
                                    data-testid={`ext-backup-delete-${t.id}`}>
                                    <Trash2 size={11} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Läufe */}
            {runs.length > 0 && (
                <div className="mt-4">
                    <div className="cm-label mb-2 flex items-center gap-1.5">
                        <Server size={12} className="text-zinc-500" /> Recent runs
                    </div>
                    <div className="space-y-1.5" data-testid="ext-backup-runs">
                        {runs.slice(0, 8).map((r) => {
                            const running = r.status === "running" || r.status === "queued";
                            const zipB = r.zip_bytes || r.bytes;
                            const dur = fmtDur(r.started_at, r.finished_at || (running ? null : r.updated_at));
                            return (
                                <div key={r._id}
                                    className="bg-zinc-950 border border-zinc-800 p-2 text-xs"
                                    data-testid={`ext-backup-run-row-${r._id}`}>
                                    <div className="flex justify-between items-start gap-2">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <StatusBadge status={runStatus(r.status)} />
                                                <span className="font-mono text-[11px] text-zinc-300">
                                                    {r.target?.name || r.target?.type || "Ad-hoc"} · {r.mode}
                                                </span>
                                                {running && (
                                                    <span className="font-mono text-[11px] text-sky-300"
                                                        data-testid={`ext-backup-run-phase-${r._id}`}>
                                                        {PHASE_LABEL[r.phase] || r.phase} {r.pct ? `${r.pct}%` : ""}
                                                    </span>
                                                )}
                                            </div>
                                            {running && (
                                                <div className="h-1 bg-zinc-800 mt-1.5 w-full overflow-hidden rounded">
                                                    <div className="h-full bg-sky-500 transition-[width] duration-500"
                                                        style={{ width: `${r.pct || 0}%` }}
                                                        data-testid={`ext-backup-run-bar-${r._id}`} />
                                                </div>
                                            )}
                                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 font-mono text-[11px] text-zinc-500"
                                                data-testid={`ext-backup-run-metrics-${r._id}`}>
                                                {r.s3_bytes > 0 && <span>S3 {fmtBytes(r.s3_bytes)}</span>}
                                                {r.db_bytes > 0 && <span>DB {fmtBytes(r.db_bytes)}</span>}
                                                {zipB > 0 && <span>ZIP {fmtBytes(zipB)}</span>}
                                                {Number(r.target_free) >= 0 && <span>Target free {fmtBytes(r.target_free)}</span>}
                                                {Number(r.scratch_free) >= 0 && <span>Scratch free {fmtBytes(r.scratch_free)}</span>}
                                                {r.vmid && <span>VM {r.vmid}{r.node ? `@${r.node}` : ""}</span>}
                                                {dur && <span>⏱ {dur}</span>}
                                            </div>
                                            <div className="font-mono text-[11px] text-zinc-600 mt-0.5">{fmtRel(r.created_at)}</div>
                                            {r.status === "error" && r.error && (
                                                <div className="text-xs text-cm-danger mt-0.5" data-testid={`ext-backup-run-error-${r._id}`}>{r.error}</div>
                                            )}
                                        </div>
                                        {r.status === "error" && (
                                            <button onClick={() => deleteRun(r._id)}
                                                title="Delete failed run"
                                                className="cm-btn-ghost text-xs flex items-center gap-1 text-cm-danger shrink-0"
                                                data-testid={`ext-backup-run-delete-${r._id}`}>
                                                <Trash2 size={11} />
                                            </button>
                                        )}
                                    </div>
                                    {(r.has_vm_log || r.vm_log) && (
                                        <details className="mt-1" data-testid={`ext-backup-run-log-${r._id}`}
                                            onToggle={(e) => { if (e.target.open) loadLog(r._id); }}>
                                            <summary className="cursor-pointer text-[11px] text-zinc-500 hover:text-zinc-300">
                                                Show VM log
                                            </summary>
                                            <pre className="mt-1 max-h-48 overflow-auto bg-black border border-zinc-800 p-2 text-[11px] text-zinc-400 whitespace-pre-wrap break-all">{logCache[r._id] ?? r.vm_log ?? "Loading …"}</pre>
                                        </details>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
